import {
    getAdditionalDrivers,
    getLeasingCompany,
    getMainDriver,
} from "@/components/features/InsuredGoods/utils/stakeholders";
import { InfoItemProps } from "@/components/organisms";
import { defaultDateFormat } from "@/core/date";
import { defaultCurrencyFormat, defaultDecimalFormat, defaultPercentFormat } from "@/core/number";
import i18n from "@/i18n";

export const panelDescriptionSchema = (details: MyBrokerApi.Vehicle, language: string): InfoItemProps[] => [
    {
        key: "brand",
        label: i18n.t("InsuredGood.Details.Vehicle.Brand"),
        value: details.brand,
    },
    {
        key: "model",
        label: i18n.t("InsuredGood.Details.Vehicle.Model"),
        value: details.model,
    },
    {
        key: "plate",
        label: i18n.t("InsuredGood.Details.Vehicle.Plate"),
        value: details.plate,
    },
    {
        key: "propertyType",
        label: i18n.t("InsuredGood.Details.Vehicle.PropertyType"),
        value: details.description?.propertyType.name,
    },
    {
        key: "genre",
        label: i18n.t("InsuredGood.Details.Vehicle.Nature"),
        value: details.description?.genre.name,
    },
    {
        key: "kmPerYear",
        label: i18n.t("InsuredGood.Details.Vehicle.KmPerYear"),
        value: defaultDecimalFormat(details.description?.kmPerYear, language),
    },
    {
        key: "horsePower",
        label: i18n.t("InsuredGood.Details.Vehicle.HorsePower"),
        value:
            details.description?.horsePower &&
            details.description?.powerType?.name &&
            `${defaultDecimalFormat(details.description?.horsePower, language)} ${
                details.description?.powerType?.name
            }`,
    },
    {
        key: "cO2",
        label: i18n.t("InsuredGood.Details.Vehicle.CO2"),
        value: defaultDecimalFormat(details.description?.cO2, language),
    },
    {
        key: "displacement",
        label: i18n.t("InsuredGood.Details.Vehicle.Displacement"),
        value: defaultDecimalFormat(details.description?.displacement, language),
    },
    {
        key: "chassis",
        label: i18n.t("InsuredGood.Details.Vehicle.Chassis"),
        value: details.description?.chassis,
    },
    {
        key: "fuel",
        label: i18n.t("Common.Fuel"),
        value: details.description?.fuel.name,
    },
    {
        key: "valueWithoutVAT",
        label: i18n.t("InsuredGood.Details.Vehicle.ValueWithoutVAT"),
        value: defaultCurrencyFormat(details.description.valueWithoutVAT, language),
    },
    {
        key: "vatExemption",
        label: i18n.t("InsuredGood.Details.Vehicle.VATExemption"),
        value: defaultPercentFormat(details.description?.vatExemption, language),
    },
];

export const panelStakeholdersSchema = (details: MyBrokerApi.Vehicle): InfoItemProps[] => [
    {
        key: "usualDriver",
        label: i18n.t("Common.MainDriver"),
        value: getMainDriver(details)?.name,
    },
    {
        key: "occasionalDriver",
        label: i18n.t("InsuredGood.Details.Vehicle.OccasionalDriver"),
        value: getAdditionalDrivers(details)
            ?.map((driver) => driver.name)
            .join(", "),
    },
    {
        key: "companyLeasingFinancing",
        label: i18n.t("InsuredGood.Details.Vehicle.CompanyLeasingFinancing"),
        value: getLeasingCompany(details)?.name,
    },
];

export const panelKeyDatesSchema = (details: MyBrokerApi.Vehicle): InfoItemProps[] => [
    {
        key: "constructionYear",
        label: i18n.t("InsuredGood.Details.Vehicle.ConstructionYear"),
        value: details.administrativeDates?.constructionYear,
        hideIfZero: true,
    },
    {
        key: "circulationDate",
        label: i18n.t("InsuredGood.Details.Vehicle.CirculationDate"),
        value: defaultDateFormat(details.administrativeDates.circulationDate),
    },
    {
        key: "registrationDate",
        label: i18n.t("InsuredGood.Details.Vehicle.RegistrationDate"),
        value: defaultDateFormat(details.administrativeDates.registrationDate),
    },
];
